import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, TextField, FormControl, InputLabel, MenuItem } from '@mui/material';
import SearchableSelect from './search/SearchableSelect';
import { previewTransactionBulk, applyTransactionBulk } from '../services/api';
import { newRequestKey } from '../utils/intake';

export default function TransactionBulkDialog({ open, transactions, action = 'delete', categories = [], onClose, onApplied }) {
  const [preview, setPreview] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const [value, setValue] = useState('');
  const generation = useRef(0), key = useRef(null), gate = useRef(false);
  const selection = transactions.map(t => t.id).sort((a, b) => a - b).join(',');
  useEffect(() => {
    generation.current += 1; key.current = open ? newRequestKey() : null; gate.current = false;
    setPreview(null); setError(''); setBusy(false); setValue('');
    return () => { generation.current += 1; };
  }, [open, selection, action]);
  const deleting = action === 'delete';
  const request = { action, transaction_ids: transactions.map(t => t.id),
    ...(action === 'date' ? { transaction_date: value } : action === 'category' ? { category_id: value } : {}) };
  const changeValue = value => { setValue(value); setPreview(null); setError(''); key.current = newRequestKey(); generation.current += 1; };
  const sameAccount = new Set(transactions.map(t => t.account_id)).size === 1;
  const run = async (apply) => {
    if (gate.current) return;
    gate.current = true; setBusy(true); setError(''); const ticket = generation.current;
    try {
      const response = apply ? await applyTransactionBulk({ ...request, fingerprint: preview.fingerprint, request_key: key.current })
        : await previewTransactionBulk(request);
      if (ticket !== generation.current) return;
      if (apply) onApplied(response.data); else setPreview(response.data);
    } catch (err) {
      if (ticket !== generation.current) return;
      if ([400, 404, 409].includes(err.response?.status)) setPreview(null);
      setError(err.response?.data?.msg || 'Could not confirm the operation. Retry the same request or refresh to check the current entries.');
    } finally { if (ticket === generation.current) { gate.current = false; setBusy(false); } }
  };
  return <Dialog open={open} onClose={() => { if (!busy) onClose(); }} fullWidth maxWidth="md">
    <DialogTitle>{deleting ? 'Delete selected transactions' : `Change selected ${action === 'date' ? 'dates' : 'categories'}`}</DialogTitle>
    <DialogContent dividers><Stack spacing={2}>
      <Typography>{transactions.length} selected. Preview the exact entries and balance impact before confirming. Split templates are not deleted. This does not cancel or send bank payments.</Typography>
      {action === 'date' && <TextField label="New transaction date" type="date" value={value} disabled={busy}
        InputLabelProps={{ shrink: true }} onChange={event => changeValue(event.target.value)} />}
      {action === 'category' && <>
        {!sameAccount && <Alert severity="warning">Select entries from one account to change their category. Accounts and amounts stay unchanged.</Alert>}
        <FormControl fullWidth disabled={busy || !sameAccount}><InputLabel id="bulk-category-label">New category</InputLabel>
          <SearchableSelect labelId="bulk-category-label" label="New category" value={open ? value : ''} onChange={event => changeValue(event.target.value)}>
            {categories.filter(c => !c.is_deleted && c.account_id === transactions[0]?.account_id).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </SearchableSelect>
        </FormControl>
      </>}
      {error && <Alert severity="error">{error}</Alert>}
      {preview && <>
        <Alert severity="warning">{deleting ? 'Deletion removes these ledger entries. An audit copy is retained; there is no automatic in-app undo.' : 'Only the chosen field changes. Amounts, accounts and split templates stay unchanged; an audit copy is retained.'}</Alert>
        {preview.blockers.map((text, i) => <Alert severity="error" key={i}>{text}</Alert>)}
        <Box aria-label="Deletion balance impact">{preview.balance_impact.length === 0 && <Typography>No balance change.</Typography>}{preview.balance_impact.map((g, i) => <Typography key={i}>
          {g.account_name} / {g.category_name}: {Number(g.change) >= 0 ? '+' : ''}{g.change}
        </Typography>)}</Box>
        <Box aria-label={deleting ? 'Selected deletion preview' : 'Selected edit preview'}>{preview.rows.map((row, i) => <Box key={row.id} sx={{ mb: 1, overflowWrap: 'anywhere' }}>
          <Typography>#{row.id} · {row.transaction_date} · {row.account_name} / {row.category_name} · {row.amount}</Typography>
          <Typography variant="body2">{row.description}</Typography>
          {!deleting && <Typography fontWeight="bold">{action === 'date' ? `${row.transaction_date} → ${preview.changes[i].after.transaction_date}` : `${row.category_name} → ${preview.changes[i].after.category_name}`}</Typography>}
        </Box>)}</Box>
      </>}
    </Stack></DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
      <Button disabled={busy} onClick={onClose}>Cancel</Button>
      <Button disabled={busy || (!deleting && !value)} onClick={() => run(false)}>{deleting ? 'Preview deletion' : 'Preview changes'}</Button>
      <Button color={deleting ? 'error' : 'primary'} variant="contained" disabled={busy || !preview || preview.blockers.length > 0}
        onClick={() => run(true)}>{deleting ? 'Delete' : 'Save'} {transactions.length} selected entries</Button>
    </DialogActions>
  </Dialog>;
}
